/**
 * Platform Configuration API Service
 *
 * Type-safe platform settings using openapi-fetch typed client.
 * Types derived from platform-admin.openapi.yaml.
 *
 * Endpoints consumed (via platform-admin spec):
 *   GET   /settings                - Get platform settings
 *   PATCH /settings                - Update platform settings
 *   GET   /settings/feature-flags  - Get feature flags
 *   PATCH /settings/feature-flags  - Update feature flags
 *
 * Legacy ConfigDomain mapping:
 *   'security' | 'notifications' | 'api' | 'data' | 'integrations' | 'defaults'
 *   These map to the single /settings endpoint (platform-admin stores them as one document).
 */

import { platformAdminClient } from '../typed-clients';
import { apiErrorMessage } from '../error-message';
import type { components } from '@aaimplatform/institutional-types/platform-admin';

export type PlatformSettings = components['schemas']['PlatformSettings'];
export type UpdatePlatformSettingsRequest = components['schemas']['UpdatePlatformSettingsRequest'];
export type FeatureFlag = components['schemas']['FeatureFlag'];

/**
 * The platform settings document, typed by the contract. A failed read throws
 * with the service's message (or the HTTP status), so a page can say why the
 * read failed instead of rendering defaults in its place.
 */
export async function getPlatformSettings(): Promise<PlatformSettings> {
  const { data, error, response } = await platformAdminClient.GET('/settings');
  const status = response.status;
  if (error || !data) {
    throw new Error(apiErrorMessage(error) ?? `Settings read failed (HTTP ${status})`);
  }
  return data;
}

/**
 * A partial update of the platform settings document. Only the fields the
 * contract declares can be sent.
 */
export async function updatePlatformSettings(
  body: UpdatePlatformSettingsRequest,
): Promise<PlatformSettings> {
  const { data, error, response } = await platformAdminClient.PATCH('/settings', { body });
  const status = response.status;
  if (error || !data) {
    throw new Error(apiErrorMessage(error) ?? `Settings save failed (HTTP ${status})`);
  }
  return data;
}

// Backward-compat type alias
export type ConfigDomain =
  | 'security'
  | 'notifications'
  | 'api'
  | 'data'
  | 'integrations'
  | 'defaults';

export const platformConfigApi = {
  /**
   * Get platform settings.
   * The domain parameter is retained for backward compatibility with the config UI,
   * but all settings are fetched in a single GET call.
   */
  get: async <T = PlatformSettings>(_domain?: ConfigDomain): Promise<T> => {
    const { data, error } = await platformAdminClient.GET('/settings');
    if (error) throw new Error('Failed to fetch platform settings');
    return data as unknown as T;
  },

  /**
   * Update platform settings.
   */
  update: async <T = PlatformSettings>(
    _domain: ConfigDomain,
    settings: Record<string, unknown>,
  ): Promise<T> => {
    const { data, error } = await platformAdminClient.PATCH('/settings', {
      body: settings as components['schemas']['UpdatePlatformSettingsRequest'],
    });
    if (error) throw new Error('Failed to update platform settings');
    return data as unknown as T;
  },

  /**
   * Get feature flags.
   */
  getFeatureFlags: async (): Promise<FeatureFlag[]> => {
    const { data, error } = await platformAdminClient.GET('/settings/feature-flags');
    if (error) throw new Error('Failed to fetch feature flags');
    return data;
  },
};
